/** @module What the machine and the data look like right now: load, memory, disks, counts. */
import { useTranslation } from "react-i18next";

import { bytes, count, duration } from "@/lib/format";
import { useQuality, useSystem } from "@/features/admin/use-admin";

/** A value with its name; the number carries the weight, the label stays quiet. */
function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1 panel panel-lg">
      <span className="t-xs t-muted">{label}</span>
      <span className="num text-[length:var(--text-xl)] font-semibold">{value}</span>
      {hint ? <span className="t-xs t-muted">{hint}</span> : null}
    </div>
  );
}

/** How full something is; the number stays readable next to it, never inside. */
function Bar({ used, total }: { used: number; total: number }) {
  const share = total > 0 ? Math.min(1, used / total) : 0;
  const tone = share > 0.9 ? "var(--loss)" : share > 0.75 ? "var(--warn)" : "var(--accent)";
  return (
    <div className="h-2 w-full overflow-hidden rounded-[var(--radius-pill)] bg-(--border)">
      <div
        className="h-full rounded-[var(--radius-pill)]"
        style={{ width: `${share * 100}%`, background: tone }}
      />
    </div>
  );
}

/** What is still missing, said plainly; a zero here is the good case. */
function Gaps() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const quality = useQuality();
  if (!quality.data) return null;

  const data = quality.data;
  const rows: { key: string; value: number; bad: boolean }[] = [
    { key: "variants_unvalued", value: data.variants_unvalued, bad: data.variants_unvalued > 0 },
    { key: "prices_suspicious", value: data.prices_suspicious, bad: data.prices_suspicious > 0 },
    {
      key: "images_missing",
      value: Math.max(0, data.prints - data.images_known),
      bad: data.prints > data.images_known,
    },
    {
      key: "prints_unindexed",
      value: Math.max(0, data.prints - data.prints_indexed),
      bad: data.prints > data.prints_indexed,
    },
    { key: "feedback_vectors", value: data.feedback_vectors, bad: false },
    { key: "scans_failed", value: data.scans_failed, bad: data.scans_failed > 0 },
  ];

  return (
    <section className="flex flex-col gap-2 panel panel-lg">
      <h3 className="t-h3">{t("admin.quality.title")}</h3>
      <p className="t-sm t-muted">{t("admin.quality.hint")}</p>
      <dl className="grid gap-x-6 gap-y-1 text-[length:var(--text-sm)] sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-baseline justify-between gap-2">
            <dt className="t-muted">{t(`admin.quality.keys.${row.key}`)}</dt>
            <dd className="num" style={{ color: row.bad ? "var(--warn)" : undefined }}>
              {count(row.value, locale)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function SystemPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const system = useSystem();

  if (system.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (system.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const data = system.data;
  const load = data.load?.[0] ?? null;
  const cores = data.cpu_count;
  const memoryShare =
    data.memory_total_bytes && data.memory_used_bytes
      ? data.memory_used_bytes / data.memory_total_bytes
      : null;

  return (
    <div className="flex flex-col gap-6">
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label={t("admin.system.load")}
          value={load === null ? "–" : load.toFixed(2)}
          hint={t("admin.system.loadHint", { cores })}
        />
        <Tile
          label={t("admin.system.memory")}
          value={bytes(data.memory_used_bytes, locale)}
          hint={
            memoryShare === null
              ? undefined
              : t("admin.system.ofTotal", {
                  total: bytes(data.memory_total_bytes, locale),
                  percent: Math.round(memoryShare * 100),
                })
          }
        />
        <Tile
          label={t("admin.system.database")}
          value={bytes(data.database_bytes, locale)}
          hint={t("admin.system.rows", { count: data.counts.prints })}
        />
        <Tile
          label={t("admin.system.uptime")}
          value={duration(data.uptime_seconds)}
          hint={new Date(data.time).toLocaleTimeString(locale)}
        />
      </section>

      <section className="flex flex-col gap-3 panel panel-lg">
        <h3 className="t-h3">{t("admin.system.disks")}</h3>
        {data.disks.map((disk) => (
          <div key={disk.path} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3 text-[length:var(--text-sm)]">
              <span className="truncate font-medium">{disk.path}</span>
              <span className="num text-(--text-muted)">
                {t("admin.system.freeOf", {
                  free: bytes(disk.free_bytes, locale),
                  total: bytes(disk.total_bytes, locale),
                })}
              </span>
            </div>
            <Bar used={disk.used_bytes} total={disk.total_bytes} />
          </div>
        ))}
      </section>

      <section className="flex flex-col gap-3 panel panel-lg">
        <h3 className="t-h3">{t("admin.system.storage")}</h3>
        <table className="w-full text-[length:var(--text-sm)]">
          <thead>
            <tr className="text-left text-(--text-muted)">
              <th className="py-1">{t("admin.system.folder")}</th>
              <th className="py-1 text-right">{t("admin.system.size")}</th>
              <th className="py-1 text-right">{t("admin.system.files")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.storage).map(([name, size]) => (
              <tr key={name} className="border-t border-(--border)">
                <td className="py-1">{t(`admin.system.folders.${name}`, name)}</td>
                <td className="num py-1 text-right">{bytes(size.bytes, locale)}</td>
                <td className="num py-1 text-right">{count(size.files, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2 panel panel-lg">
          <h3 className="t-h3">{t("admin.system.workers")}</h3>
          <ul className="flex flex-col gap-1 text-[length:var(--text-sm)]">
            {Object.entries(data.workers).map(([queue, alive]) => (
              <li key={queue} className="flex items-center justify-between gap-3">
                <span className="truncate">{queue.replace("pokescan:queue:", "")}</span>
                <span className="flex items-center gap-2">
                  <span className="num text-(--text-muted)">
                    {t("admin.system.waiting", { count: data.queue_depth[queue] ?? 0 })}
                  </span>
                  {/* Word and colour together; a dot alone would say nothing to a screen reader. */}
                  <span style={{ color: alive ? "var(--gain)" : "var(--loss)" }}>
                    {alive ? t("status.workers.alive") : t("status.workers.missing")}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-2 panel panel-lg">
          <h3 className="t-h3">{t("admin.system.data")}</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[length:var(--text-sm)]">
            {(
              [
                "sets",
                "prints",
                "variants",
                "prices",
                "embeddings",
                "items",
                "scans",
                "reviews_open",
                "users",
              ] as const
            ).map((key) => (
              <div key={key} className="flex items-baseline justify-between gap-2">
                <dt className="t-muted">{t(`admin.system.counts.${key}`)}</dt>
                <dd className="num">{count(data.counts[key], locale)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Gaps />
    </div>
  );
}
